'use client';

import React, { useCallback, useState } from 'react';
import { useLocale } from 'next-intl';
import { subscribeMail } from '@/services/salesforece-mail';
import { validateEmail } from '@/utils';
import { generateSubscribeDataString } from '@/utils/landing-attribution';
import { sendSentryError } from '@lib/sentry';
import { useGenerateLeadGtag } from './useGtag.hook';

export type NewsletterMessageType =
  | 'fetch-success'
  | 'fetch-error'
  | 'already-subscribed'
  | 'invalid-email'
  | null;

// 이메일 해시 함수 (민감정보 보호)
const hashEmail = (email: string): string => {
  const parts = email.split('@');
  if (parts.length !== 2) return '***';
  const username = parts[0];
  const domain = parts[1];
  return `${username.slice(0, 2)}***@${domain}`;
};

type UseNewsletterSubscribeParams = {
  /** generate_lead 의 event_label — 구독 경로(본문 폼/팝업)를 구분한다 */
  leadLabel: string;
  /** Sentry source */
  source: string;
  /**
   * 구독 성공 시 호출 (완료 모달 오픈 등).
   *
   * 넘기면 성공 `messageType` 을 세우지 않는다 — 성공 피드백을 이 콜백이
   * 책임지므로 인라인 메시지까지 뜨면 중복 안내가 된다. 억제 판단을 소비처가
   * 각자 하면 조건식이 갈라지므로 훅에서 한 번만 정한다.
   */
  onSubscribed?: () => void;
};

/**
 * 뉴스레터 구독 폼의 상태와 전송을 담당한다.
 *
 * 본문/레일 폼(`NewsletterSubscribe`)과 블로그 상세 모바일 팝업이 같은 전송 로직을
 * 쓴다. 표시(마크업)는 각 컴포넌트가 갖는다.
 */
export const useNewsletterSubscribe = ({
  leadLabel,
  source,
  onSubscribed,
}: UseNewsletterSubscribeParams) => {
  const locale = useLocale();
  const addLeadGtag = useGenerateLeadGtag({ label: leadLabel });

  const [email, setEmail] = useState('');
  const [messageType, setMessageType] = useState<NewsletterMessageType>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [submittedEmail, setSubmittedEmail] = useState('');

  const handleEmailChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setEmail(e.target.value);
      setMessageType(null);
    },
    [],
  );

  const handleSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();

      if (!validateEmail(email)) {
        setMessageType('invalid-email');
        return;
      }

      if (isLoading) return;

      if (submittedEmail === email) {
        setMessageType('already-subscribed');
        return;
      }

      setIsLoading(true);
      setMessageType(null);

      try {
        const data = generateSubscribeDataString(email);
        await subscribeMail(data, locale);
        setSubmittedEmail(email);
        // 성공하면 입력을 비운다 — 중복 구독 차단은 `submittedEmail` 이 따로
        // 들고 있으므로 같은 주소를 다시 입력해도 already-subscribed 로 걸린다
        setEmail('');

        setMessageType(onSubscribed ? null : 'fetch-success');
        addLeadGtag('success');
        onSubscribed?.();
      } catch (error) {
        console.error('Newsletter subscription failed', error);
        sendSentryError({
          error,
          source,
          extra: { emailHash: hashEmail(email), locale },
        });
        setMessageType('fetch-error');
        addLeadGtag('fail');
      } finally {
        setIsLoading(false);
      }
    },
    [
      email,
      isLoading,
      submittedEmail,
      locale,
      source,
      addLeadGtag,
      onSubscribed,
    ],
  );

  return {
    email,
    messageType,
    isLoading,
    handleEmailChange,
    handleSubmit,
  };
};
